// Copyright (c) 2021-2024. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { Helmet } from "react-helmet-async";
import { PUBLIC_URL } from "../../../../core/constants/frontend";
import { THEME_2022_CLASS_NAME } from "../../../../core/constants/className";
import { PageImage } from "../../../core/types/PageImage";
import { OgType } from "../../../core/types/OgType";
import { SendanorSeoImageData } from "../../../../core/types/SendanorSeoImageData";
import { COMPANY_TWITTER_NAME } from "../../../../core/constants/company";
import { useSEOProps } from "./useSEOProps";

export interface SEOProps {
    readonly title             : string;
    readonly description       : string;
    readonly siteName         ?: string;
    readonly price            ?: string;
    readonly buttonText       ?: string;
    readonly discountText     ?: string;
    readonly extraInfo        ?: string;
    readonly tagLine          ?: string;
    readonly ogType           ?: OgType;
    readonly ogImage          ?: PageImage | SendanorSeoImageData;
}

export function SEO (props: SEOProps) {
    const seo = useSEOProps(props);
    return (
        <Helmet>
            <html className={THEME_2022_CLASS_NAME} lang={seo.language} />
            <title>{seo.title} - {seo.siteName}</title>
            <meta name="description" content={seo.description} />
            <base target="_blank" href={`${PUBLIC_URL}/`} />
            <link rel="canonical" href={seo.pageUrl} />
            <meta property="og:type" content={seo.ogType} />
            <meta property="og:site_name" content={seo.siteName} />
            <meta property="og:updated_time" content={new Date().toISOString()} />
            <meta property="og:ttl" content={seo.ogTTL.toFixed(0)} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:locale" content={seo.language} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content={seo.pageImageUrl} />
            {seo.ogImageWidth !== undefined ? <meta property="og:image:width" content={seo.ogImageWidth} /> : null}
            {seo.ogImageHeight !== undefined ? <meta property="og:image:height" content={seo.ogImageHeight} /> : null}
            {seo.ogImageType !== undefined ? <meta property="og:image:type" content={seo.ogImageType} /> : null}
            <meta property="og:url" content={seo.pageUrl} />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={seo.pageImageUrl} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={`@${COMPANY_TWITTER_NAME}`} />
            <meta name="robots" content="index, follow" />
        </Helmet>
    );

}
